import * as Icons from "lucide-react";
import SectionHeader from "./SectionHeader";
import Reveal from "./Reveal";
import { HOME_WRAP, SECTION_Y } from "./homeTheme";

/**
 * "Why Shingare" — an asymmetric bento grid instead of six identical boxes. The
 * first feature is the large navy anchor card; the rest alternate sizes across a
 * 6-column grid (2+2+2 / 2+4 / 3+3). Content is `data.features` (Admin → Home
 * Page): heading, icon, description — only claims already on the site.
 */
const SPANS = [
  "lg:col-span-2 lg:row-span-2", // 0 anchor
  "lg:col-span-2", // 1
  "lg:col-span-2", // 2
  "lg:col-span-4", // 3 wide
  "lg:col-span-3", // 4
  "sm:col-span-2 lg:col-span-3", // 5 (full row on 2-col tablets so the grid never ends on an orphan)
];

export default function WhyShingare({ data }) {
  const section = data?.featuresSection || {};
  const items = (Array.isArray(data?.features) ? data.features : []).filter((f) => f.status !== false).sort((a, b) => (a.order || 0) - (b.order || 0)).slice(0, 6);
  if (!items.length) return null;
  return (
    <section className={`bg-white ${SECTION_Y}`} aria-labelledby="home-why-title">
      <div className={HOME_WRAP}>
        <SectionHeader eyebrow={section.title || "Why Shingare"} title={section.subtitle || "Engineering Excellence You Can Trust"} titleId="home-why-title" className="mb-12 lg:mb-16" />
        <div className="grid grid-cols-1 gap-4 sm:grid-cols-2 lg:grid-cols-6 lg:gap-5">
          {items.map((f, i) => {
            const Icon = (f.icon && Icons[f.icon]) || Icons.Cog;
            const anchor = i === 0;
            return (
              <Reveal
                key={`${f.heading}-${i}`}
                delay={i * 0.05}
                y={20}
                className={`${SPANS[i] || "lg:col-span-2"} ${anchor ? "sm:col-span-2" : ""}`}
              >
                <article
                  className={`group relative h-full overflow-hidden rounded-3xl p-7 transition-all duration-300 hover:-translate-y-1 sm:p-9 ${
                    anchor
                      ? "flex min-h-[320px] flex-col justify-between bg-[#071B3A] text-white shadow-[0_30px_60px_-30px_rgba(7,27,58,0.7)] lg:min-h-full"
                      : "bg-[#F7F9FC] text-[#13233F] hover:bg-[#EFF4FF]"
                  }`}
                >
                  <Icon
                    className={`pointer-events-none absolute -right-6 -top-6 h-40 w-40 transition-transform duration-500 group-hover:scale-105 ${anchor ? "text-white/[0.06]" : "text-[#1769FF]/[0.07]"}`}
                    strokeWidth={1}
                    aria-hidden="true"
                  />
                  <span className={`relative flex h-12 w-12 items-center justify-center rounded-xl ${anchor ? "bg-[#1769FF] text-white" : "bg-white text-[#1769FF] shadow-[0_6px_18px_-8px_rgba(7,27,58,0.35)]"}`}>
                    <Icon className="h-6 w-6" strokeWidth={1.6} aria-hidden="true" />
                  </span>
                  <div className={`relative ${anchor ? "mt-16" : "mt-8"}`}>
                    <h3 className={`font-heading font-semibold tracking-[-0.01em] ${anchor ? "text-[26px] leading-tight" : "text-[20px]"}`}>{f.heading}</h3>
                    {f.description && <p className={`mt-3 max-w-md text-[15px] leading-relaxed ${anchor ? "text-white/70" : "text-[#667085]"}`}>{f.description}</p>}
                  </div>
                </article>
              </Reveal>
            );
          })}
        </div>
      </div>
    </section>
  );
}
